<script setup lang="ts">
// ShouldIReplaceMyAC.com — "Get a Straight Answer". Scoreboard single-page
// stack: Hero + stats → Decision Wizard → The Math → What Changes the Math →
// Get Quotes (shared contact schema) → Footer.
import SiteConfigProvider from '@/shared/naive/SiteConfigProvider.vue'
import SiteFooter from '@/shared/components/SiteFooter.vue'
import PhotoBand from '@/shared/components/PhotoBand.vue'
// Pixabay Content License (free commercial use, no attribution required).
import duskPhoto from '@/assets/shouldi-dusk.jpg'
import HeroSection from './sections/HeroSection.vue'
import WizardSection from './sections/WizardSection.vue'
import MathSection from './sections/MathSection.vue'
import FactorsSection from './sections/FactorsSection.vue'
import ContactSection from './sections/ContactSection.vue'
import { scrollTo } from './store'

const NAV = [
    { id: 'wizard', label: 'The Wizard' },
    { id: 'math', label: 'The Math' },
    { id: 'factors', label: 'What Changes It' },
    { id: 'contact', label: 'Get Quotes' },
]
</script>

<template>
    <SiteConfigProvider>
        <header class="topnav">
            <div class="site-content bar">
                <span class="brand">
                    <svg class="mark" viewBox="0 0 28 28" aria-hidden="true">
                        <rect x="1" y="1" width="26" height="26" rx="5" fill="#17348c" />
                        <path d="M7.5 14.5l4.5 4.5 8.5-9.5" fill="none" stroke="#fff" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round" />
                        <rect x="19" y="1" width="8" height="8" rx="2.5" fill="#d64541" />
                    </svg>
                    <span class="word">SHOULD I REPLACE MY <em>AC</em>?</span>
                </span>
                <nav>
                    <button
                        v-for="item in NAV.filter((i) => i.id !== 'wizard')"
                        :key="item.id"
                        type="button"
                        @click="scrollTo(item.id)"
                    >
                        {{ item.label }}
                    </button>
                </nav>
                <button class="cta nav-cta" type="button" @click="scrollTo('wizard')">
                    Get My Answer
                </button>
            </div>
        </header>

        <main>
            <HeroSection />
            <WizardSection />
            <MathSection />
            <FactorsSection />
            <PhotoBand
                :image="duskPhoto"
                overlay="linear-gradient(180deg, rgba(21, 39, 71, 0.82) 0%, rgba(23, 52, 140, 0.55) 55%, rgba(21, 39, 71, 0.85) 100%)"
            >
                <p class="band-head">Stop guessing.<br />Run the numbers.</p>
                <p class="band-sub">
                    Two minutes. Six questions. The same math a good technician would do on your
                    driveway — minus the sales pitch.
                </p>
                <button class="cta band-cta" type="button" @click="scrollTo('wizard')">
                    Get my answer →
                </button>
            </PhotoBand>
            <ContactSection />
        </main>
        <SiteFooter
            site-name="ShouldIReplaceMyAC.com"
            tagline="One question, answered straight — published math, every factor shown, no games."
        />
    </SiteConfigProvider>
</template>

<style scoped lang="scss">
.topnav {
    position: sticky;
    top: 0;
    z-index: 10;
    background: var(--site-bg);
    border-bottom: 2px solid var(--site-text);

    .bar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0.7rem var(--site-space-lg);
    }

    .brand {
        display: inline-flex;
        align-items: center;
        gap: 0.55rem;
        font-family: var(--site-font-heading);
        font-size: 0.95rem;
        letter-spacing: 0.02em;

        .mark {
            width: 1.7rem;
            height: 1.7rem;
            flex-shrink: 0;
        }

        em {
            color: var(--site-danger);
            font-style: normal;
        }
    }

    .nav-cta {
        padding: 0.5em 1.2em;
        font-size: 0.85rem;
        letter-spacing: 0.04em;
        text-transform: uppercase;
    }

    nav {
        display: flex;
        gap: var(--site-space-xs);

        button {
            background: none;
            border: none;
            cursor: pointer;
            font-family: var(--site-font-body);
            font-weight: 600;
            font-size: 0.92rem;
            color: var(--site-text-muted);
            padding: 0.4rem 0.7rem;
            border-radius: var(--site-radius-sm);
            text-transform: uppercase;
            letter-spacing: 0.04em;

            &:hover {
                color: var(--site-accent);
                background: var(--site-accent-soft);
            }
        }
    }
}

@media (max-width: 700px) {
    .topnav nav {
        display: none;
    }
}
</style>


<style lang="scss">
// Band typography (unscoped: PhotoBand renders the slot).
#factors + .photo-band {
    .band-head {
        font-family: var(--site-font-heading);
        font-size: clamp(2rem, 5vw, 3.4rem);
        line-height: 1.08;
        color: #fff;
        margin: 0;
        text-shadow: 0 2px 24px rgba(0, 0, 0, 0.35);
    }

    .band-sub {
        color: rgba(255, 255, 255, 0.85);
        font-size: 1.1rem;
        max-width: 46ch;
        margin: 0;
    }

    .band-cta {
        margin-top: var(--site-space-sm);
        font-size: 1.1rem;
    }
}
</style>
